import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Button, Chip, MenuItem, Paper, TextField, Tooltip, Typography } from '@mui/material';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import axios from 'axios';

import { RootState } from '../redux/store';
import { BFF_BASE_URL } from '../auth/authApi';
import { PromptVersionDialog } from '../prompts/components/PromptVersionDialog';
import { ReplayBatchDialog } from '../replays/components/ReplayBatchDialog';
import { ReplayDialog } from '../replays/components/ReplayDialog';
import { useSelectedUser } from '../users/hooks/useSelectedUser';
import { LoadingButton } from '../shared/components/LoadingButton';
import { Section } from '../shared/components/Section';
import { EmojiText } from '../shared/components/EmojiText';
import { GlowAlert } from '../shared/components/GlowAlert';

const PAGE_SIZE = 50;

interface AdminMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  channel?: string | null;
  status?: string | null;
  model?: string | null;
  provider?: string | null;
  prompt_versions?: Record<string, { id: string | null; version: number | null }> | null;
  feedback_type?: 'up' | 'down' | null;
  feedback_text?: string | null;
}

interface MessagePage {
  items: AdminMessage[];
  next_cursor: string | null;
}

const UserMessagesPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const [selectedUserId, setSelectedUserId] = useSelectedUser();
  const navigate = useNavigate();
  const users = useSelector((state: RootState) => state.users.users);
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openVersion, setOpenVersion] = useState<{
    key: string;
    id: string | null;
    version: number | null;
  } | null>(null);
  const [replayFor, setReplayFor] = useState<string | null>(null);
  const [batchOpen, setBatchOpen] = useState(false);

  const load = useCallback(
    async (cursor?: string) => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get<MessagePage>(
          `${BFF_BASE_URL}/admin/users/${encodeURIComponent(userId ?? '')}/messages`,
          {
            params: {
              cursor,
              limit: PAGE_SIZE,
              status: status || undefined,
              channel: channel || undefined,
            },
          }
        );
        setMessages((prev) => (cursor ? [...prev, ...response.data.items] : response.data.items));
        setNextCursor(response.data.next_cursor);
      } catch (err) {
        setError(
          axios.isAxiosError(err)
            ? err.response?.data?.error || err.message
            : 'Failed to load messages'
        );
      } finally {
        setLoading(false);
      }
    },
    [userId, status, channel]
  );

  // The address wins when it names a user; otherwise the remembered user is opened.
  useEffect(() => {
    if (userId) {
      if (userId !== selectedUserId) setSelectedUserId(userId);
    } else if (selectedUserId) {
      navigate(`/messages/${selectedUserId}`, { replace: true });
    }
  }, [userId, selectedUserId, setSelectedUserId, navigate]);

  useEffect(() => {
    setMessages([]);
    setNextCursor(null);
    load();
  }, [load]);

  return (
    <Section title="Message history" onRefresh={() => load()} refreshing={loading}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <TextField
          select
          size="small"
          label="User"
          value={users.some((u) => u.id === userId) ? userId : ''}
          onChange={(e) => navigate(`/messages/${e.target.value}`)}
          sx={{ minWidth: 280 }}
        >
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.display_name || u.email || u.id}
              {u.message_count !== undefined ? ` (${u.message_count})` : ''}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Result"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 160 }}
          helperText={status ? 'User messages only' : undefined}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="answered">Succeeded</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="processing">Processing</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Origin"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="web">Web</MenuItem>
          <MenuItem value="whatsapp">WhatsApp</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={() => setBatchOpen(true)}>
          Replay a batch
        </Button>
      </Box>

      {!userId && <Typography color="text.secondary">Select a user to see their messages.</Typography>}

      {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}

      {messages.map((m) => (
        <Paper
          key={m.id}
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 1,
            backgroundColor: 'background.paper',
            borderLeft: 4,
            borderLeftColor: m.role === 'user' ? 'primary.main' : 'secondary.main',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">{m.role === 'user' ? 'User' : 'VictorIA'}</Typography>
            {m.channel && <Chip size="small" label={m.channel} />}
            {m.model && <Chip size="small" variant="outlined" label={m.provider ? `${m.provider} / ${m.model}` : m.model} />}
            {m.prompt_versions &&
              Object.entries(m.prompt_versions).map(([key, v]) => (
                <Chip
                  key={key}
                  size="small"
                  variant="outlined"
                  label={`${key} v${v.version ?? '?'}`}
                  onClick={() => setOpenVersion({ key, id: v.id, version: v.version })}
                />
              ))}
            {m.role === 'assistant' && (
              <Chip size="small" color="primary" variant="outlined" label="Replay" onClick={() => setReplayFor(m.id)} />
            )}
            {m.status === 'failed' && <Chip size="small" color="error" label="failed" />}
            <Typography variant="caption" color="text.secondary">
              {new Date(m.timestamp).toLocaleString()}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            <EmojiText>{m.content}</EmojiText>
          </Typography>
          {m.role === 'assistant' && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
              {m.feedback_type === 'up' ? (
                <ThumbUpIcon aria-label="Thumbs up" sx={{ fontSize: 16 }} />
              ) : (
                <ThumbUpOffAltIcon aria-label="No thumbs up" sx={{ fontSize: 16, opacity: 0.5 }} />
              )}
              {m.feedback_type === 'down' ? (
                <Tooltip
                  title={
                    <span style={{ whiteSpace: 'pre-line' }}>
                      {m.feedback_text || 'No category or reason given'}
                    </span>
                  }
                >
                  <ThumbDownIcon aria-label="Thumbs down" sx={{ fontSize: 16 }} />
                </Tooltip>
              ) : (
                <ThumbDownOffAltIcon aria-label="No thumbs down" sx={{ fontSize: 16, opacity: 0.5 }} />
              )}
            </Box>
          )}
        </Paper>
      ))}

      {!loading && !error && messages.length === 0 && (
        <Typography color="text.secondary">No messages yet.</Typography>
      )}
      {loading && messages.length === 0 && <GlowAlert severity="info">Loading messages...</GlowAlert>}
      {nextCursor && (
        <LoadingButton variant="outlined" loading={loading} onClick={() => load(nextCursor)}>
          Load older messages
        </LoadingButton>
      )}
      {batchOpen && (
        <ReplayBatchDialog
          userId={userId}
          userName={users.find((u) => u.id === userId)?.display_name}
          onClose={() => setBatchOpen(false)}
        />
      )}
      {replayFor && <ReplayDialog messageId={replayFor} onClose={() => setReplayFor(null)} />}
      {openVersion && (
        <PromptVersionDialog
          promptKey={openVersion.key}
          versionId={openVersion.id}
          version={openVersion.version}
          onClose={() => setOpenVersion(null)}
        />
      )}
    </Section>
  );
};

export default UserMessagesPage;
